import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Bell, MapPin, ArrowRight, Settings, Heart, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";

const META = {
  zona: { icon: MapPin, label: "Zona", cls: "bg-secondary/15 text-secondary" },
  wishlist: { icon: Heart, label: "Desideri", cls: "bg-rose-100 text-rose-600 dark:bg-rose-950 dark:text-rose-300" },
};

export default function Avvisi() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("all");

  const load = useCallback(async () => {
    const { data } = await api.get("/notifications");
    const mine = data.filter((n) => n.type === "zona" || n.type === "wishlist");
    setItems(mine);
    return mine;
  }, []);

  useEffect(() => {
    (async () => {
      const mine = await load();
      if (mine.some((n) => !n.read)) {
        try {
          await api.post("/notifications/read", { types: ["zona", "wishlist"] });
          setItems((arr) => arr.map((n) => ({ ...n, read: true })));
        } catch { /* ignore */ }
      }
    })();
  }, [load]);

  const hasUnread = items.some((n) => !n.read);
  const markAllRead = async () => {
    try {
      await api.post("/notifications/read", { types: ["zona", "wishlist"] });
      setItems((arr) => arr.map((n) => ({ ...n, read: true })));
      toast.success("Avvisi segnati come letti");
    } catch { toast.error("Operazione non riuscita"); }
  };

  const filtered = tab === "all" ? items : items.filter((n) => n.type === tab);
  const counts = {
    all: items.length,
    zona: items.filter((n) => n.type === "zona").length,
    wishlist: items.filter((n) => n.type === "wishlist").length,
  };

  const TabBtn = ({ id, label }) => (
    <button data-testid={`avvisi-tab-${id}`} onClick={() => setTab(id)}
      className={`h-9 px-3 rounded-full text-sm border transition-all ${tab === id ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/40"}`}>
      {label} <span className="opacity-70">({counts[id]})</span>
    </button>
  );

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-secondary/15 text-secondary flex items-center justify-center"><Bell className="w-6 h-6" /></div>
          <h1 className="text-2xl sm:text-3xl font-serif font-bold tracking-tight">I miei avvisi</h1>
        </div>
        <Button variant="outline" size="sm" asChild><Link to="/profilo" data-testid="avvisi-manage"><Settings className="w-4 h-4 mr-2" />Gestisci comuni</Link></Button>
      </div>
      <p className="text-sm text-muted-foreground mb-5">Nuovi libri nei comuni che segui e libri dalla tua lista dei desideri.</p>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        <TabBtn id="all" label="Tutti" />
        <TabBtn id="zona" label="Zona" />
        <TabBtn id="wishlist" label="Desideri" />
        {hasUnread && (
          <Button variant="ghost" size="sm" onClick={markAllRead} data-testid="avvisi-mark-read" className="ml-auto text-secondary">
            <CheckCheck className="w-4 h-4 mr-2" />Segna tutti come letti
          </Button>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 rounded-2xl border border-dashed border-border text-muted-foreground">
          <Bell className="w-9 h-9 mx-auto mb-3" />
          Nessun avviso per ora. <Link to="/profilo" className="text-primary underline">Segui un comune</Link> o aggiungi libri alla <Link to="/desideri" className="text-primary underline">lista dei desideri</Link>.
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((n) => {
            const m = META[n.type] || META.zona;
            const Icon = m.icon;
            return (
              <Link key={n.id} to={n.link || "#"} data-testid={`avviso-${n.id}`}
                className="flex items-center gap-3 rounded-xl border border-border bg-card p-4 hover:border-primary/40 transition-colors">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${m.cls}`}><Icon className="w-5 h-5" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded ${m.cls}`}>{m.label}</span>
                    {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-primary" />}
                  </div>
                  <p className="text-sm mt-1">{n.text}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{new Date(n.created_at).toLocaleString("it-IT")}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
